/**
 * Demo log-ins. Any password works; the email picks the persona (unknown
 * emails get the CEO). Each persona holds ONE role, as real users do, so the
 * navigation shows exactly what that kind of user would see.
 */
import { LEADERSHIP, ORG, fakeUlid } from './world';

export interface Persona {
    key: string;
    email: string;
    name: string;
    title: string;
    roles: string[];
    roleLabels: string[];
}

const at = (local: string) => `${local}@${ORG.domain}`;

export const PERSONAS: Persona[] = [
    { key: 'ceo', email: at('ceo'), name: LEADERSHIP.ceo.name, title: LEADERSHIP.ceo.title, roles: ['ROLE_ADMIN'], roleLabels: ['Admin'] },
    { key: 'coo', email: at('operations'), name: LEADERSHIP.coo.name, title: LEADERSHIP.coo.title, roles: ['ROLE_MANAGER'], roleLabels: ['Management'] },
    { key: 'care', email: at('clientservices'), name: LEADERSHIP.care.name, title: LEADERSHIP.care.title, roles: ['ROLE_CSC'], roleLabels: ['Client Services'] },
    { key: 'hr', email: at('people'), name: LEADERSHIP.hr.name, title: LEADERSHIP.hr.title, roles: ['ROLE_HR'], roleLabels: ['HR'] },
    { key: 'board', email: at('board'), name: LEADERSHIP.chair.name, title: LEADERSHIP.chair.title, roles: ['ROLE_BOARD'], roleLabels: ['Board'] },
];

export const DEFAULT_PERSONA = PERSONAS[0];

export function personaByEmail(email: string | null | undefined): Persona {
    const e = (email || '').trim().toLowerCase();
    return PERSONAS.find(p => p.email === e) || PERSONAS.find(p => e.startsWith(`${p.key}@`) || e.startsWith(p.email.split('@')[0] + '@')) || DEFAULT_PERSONA;
}

/** The serialized User the backend returns from login and /api/v1/user/current. */
export function userJson(p: Persona): Record<string, unknown> {
    return {
        id: fakeUlid(`persona:${p.key}`),
        string: p.name,
        name: p.name,
        archived: false,
        photo: null,
        email: p.email,
        roles: p.roles,
        locked: false,
        phone: null,
        roleLabels: p.roleLabels,
        jobTitle: p.title,
    };
}

const b64u = (o: object) => btoa(JSON.stringify(o)).replace(/=+$/, '').replace(/\+/g, '-').replace(/\//g, '_');

/** A well-formed JWT (signature is never checked client-side) that does not expire for 10 years. */
export function demoToken(p: Persona): string {
    const iat = Math.floor(Date.now() / 1000);
    return `${b64u({ typ: 'JWT', alg: 'RS256' })}.${b64u({ iat, exp: iat + 10 * 365 * 86400, roles: p.roles, userIdentifier: p.email })}.demo-signature`;
}

/** Persona for a request: impersonation header first, then the bearer token. */
export function personaFromHeaders(switchUser: string | null, authorization: string | null): Persona {
    if (switchUser) {
        return personaByEmail(switchUser);
    }
    const token = (authorization || '').replace(/^Bearer\s+/i, '');
    const payload = token.split('.')[1];
    if (payload) {
        try {
            const json = JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/')));
            return personaByEmail(json.userIdentifier);
        } catch {
            // fall through
        }
    }
    return DEFAULT_PERSONA;
}
